import { useTranslation } from 'react-i18next';
import { useActiveCampaign } from '@/api/recognition';
import { CAMPAIGN_NAV_ID } from './recognitionNavLinks';

/**
 * RF-074 / WP-B2 — cómo se llama y qué glifo lleva en el menú la entrada de la campaña personalizada.
 *
 * 🔴 El defecto que cierra: el paso «Diseño» del alta de campañas escribe `menuIcon` y `menuTitle` en
 * `CampaignDto.diseno` (`AdminCampaigns.tsx:1099-1105`) y **nadie los leía**. El menú mostraba
 * siempre el 🚀 y el texto "Innovación" escritos en duro en `RECOGNITION_NAV_LINKS`, de modo que un
 * administrador podía configurar el nombre de su campaña, guardarlo sin error, y no ver ningún
 * cambio en ninguna parte.
 *
 * Vive aparte del `Sidebar` a propósito: el menú es UNO para las dos zonas (portal y módulo) y la
 * regla de "cómo se llama esto en el menú" tiene que dar la misma respuesta en las dos. Duplicarla
 * las haría divergir en cuanto alguien tocara una.
 */
export interface CampaignNavEntry {
  /** El emoji configurado, o el de la entrada por defecto si la campaña no puso ninguno. */
  emoji?: string;
  /** El título configurado, o `undefined` para que el llamador use su clave i18n. */
  titulo?: string;
}

/**
 * @param ou OU del usuario. Sin ella no hay campaña activa que resolver y se devuelve el default.
 * @param emojiPorDefecto El de `RECOGNITION_NAV_LINKS`, que sigue mandando cuando no hay campaña.
 */
export function useCampaignNavEntry(
  ou: string | undefined, emojiPorDefecto?: string,
): CampaignNavEntry {
  const { i18n } = useTranslation();
  const { data: campaña } = useActiveCampaign(ou);

  // 🔴 SOLO la campaña PERSONALIZADA renombra la entrada, y son dos cosas distintas las que esto
  // arregla.
  //
  // 1. `GET v1/campaigns/active` devuelve la campaña activa de la OU SEA DEL TIPO QUE SEA, mientras
  //    que la página a la que enlaza esta entrada (`InnovacionView`) busca explícitamente la de tipo
  //    `Personalizada`. Sin filtrar, con una campaña Brillamos en ventana el menú volvía al texto
  //    genérico mientras la página seguía mostrando la personalizada: el renombrado funcionaba a
  //    ratos, según el calendario.
  // 2. REQ-034 — la identidad de Brillamos es INMUTABLE. Hoy el formulario bloquea el paso Diseño
  //    para las de Brillamos, pero `diseno` es un diccionario libre que el backend no valida: un
  //    `menuTitle` llegado por seed, importación o API renombraría la entrada. La guarda tiene que
  //    estar en el LECTOR, no en un formulario.
  const diseno = campaña?.tipo === 'Personalizada' ? campaña.diseno : undefined;

  // 🔴 Mientras la consulta está en vuelo o falló, `campaña` es `undefined` y se devuelve el default.
  // No es un descuido: el menú se pinta en cada carga, y dejar la entrada sin nombre —o parpadeando
  // entre dos— es peor que enseñar el texto genérico un instante. Un fallo de red no puede hacer
  // desaparecer una entrada del menú.
  if (!diseno) return { emoji: emojiPorDefecto };

  return {
    emoji: limpiar(diseno.menuIcon) ?? emojiPorDefecto,
    titulo: tituloEnIdioma(diseno.menuTitle, i18n.language),
  };
}

/**
 * Reconocer, Brillamos y la campaña personalizada (`innovacion`) son mutuamente excluyentes: el
 * backend nunca deja más de una campaña `Activa` por OU (`GetActiveByOuAsync`), así que el menú
 * debe mostrar solo la entrada que corresponde — nunca las tres a la vez. Esto solo TRADUCE la
 * campaña activa (o su ausencia) al id de `RECOGNITION_NAV_LINKS` que debe verse; el filtrado en
 * el menú lo hace cada llamador contra `EXCLUSIVE_RECOGNITION_NAV_IDS`.
 *
 * Mientras la consulta está en vuelo, sin OU, o si falló, se asume "sin campaña" (Reconocer): el
 * mismo default que aplica el backend cuando `GET /v1/campaigns/active` devuelve 204.
 */
export function useActiveRecognitionEntryId(ou: string | undefined): string {
  const { data: campaña } = useActiveCampaign(ou);
  if (campaña?.estado === 'Activa' && campaña.tipo === 'Brillamos') return 'brillamos';
  if (campaña?.estado === 'Activa' && campaña.tipo === 'Personalizada') return CAMPAIGN_NAV_ID;
  return 'reconocer';
}

/**
 * Lo mismo que `useActiveRecognitionEntryId`, más si la respuesta todavía no se sabe. Lo necesita
 * quien GATEA una pantalla (no un menú): mientras la campaña está en vuelo, `useActiveRecognitionEntryId`
 * ya devuelve `'reconocer'` por defecto, y una pantalla que redirija con ese valor provisional pintaría
 * el reconocimiento diario un instante antes de irse a Brillamos.
 *
 * Se apoya en el hook de arriba en vez de repetir la regla: las dos respuestas tienen que ser la misma.
 * `useActiveCampaign` comparte clave de caché, así que no dispara una segunda petición.
 */
export function useRecognitionEntryGate(ou: string | undefined): { entryId: string; isLoading: boolean } {
  const entryId = useActiveRecognitionEntryId(ou);
  // Sin OU la consulta queda deshabilitada y nunca resuelve: `isLoading` de TanStack ya es `false`
  // ahí (`isPending && isFetching`), así que la pantalla no se queda esperando para siempre.
  const { isLoading } = useActiveCampaign(ou);
  return { entryId, isLoading };
}

/** Vacío o solo espacios cuenta como "no configurado": un título en blanco borraría la entrada. */
function limpiar(valor: string | undefined): string | undefined {
  const v = valor?.trim();
  return v ? v : undefined;
}

/**
 * El título puede venir como texto plano o como JSON por idioma (`{"es":"…","en":"…"}`), que es como
 * el resto del módulo guarda los campos traducibles.
 *
 * Se acepta el texto plano porque el campo del formulario es un `input` simple: quien escriba
 * "Innovación LATAM" a secas tiene que ver "Innovación LATAM", no un error ni una cadena vacía.
 */
function tituloEnIdioma(valor: string | undefined, idioma: string): string | undefined {
  const bruto = limpiar(valor);
  if (!bruto) return undefined;
  if (!bruto.startsWith('{')) return bruto;

  try {
    const porIdioma = JSON.parse(bruto) as Record<string, string>;
    const base = idioma.split('-')[0];
    return limpiar(porIdioma[idioma] ?? porIdioma[base] ?? porIdioma.es ?? porIdioma.en)
      // Un JSON sin ninguno de los idiomas conocidos no puede dejar la entrada sin nombre: se toma
      // el primer valor no vacío que traiga.
      ?? limpiar(Object.values(porIdioma).find((v) => limpiar(v)));
  } catch {
    // JSON mal formado: se enseña tal cual. Es feo, pero es lo que el administrador escribió, y
    // esconderlo le impediría darse cuenta de que se equivocó.
    return bruto;
  }
}
